import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Save, User, FileText, ScanLine, Loader2, Check,
  CreditCard, IdCard, Eye, Trash2, Building2, Download,
  AlertTriangle, CheckCircle, XCircle, ShieldCheck, ShieldAlert, Truck, Wallet
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Categories de permis Cote d'Ivoire
const PERMIT_CATEGORIES_INFO = {
  A: { label: 'A', desc: 'Motos / Tricycles', critical: false },
  B: { label: 'B', desc: 'Vehicule leger (max 3.5T)', critical: false },
  C: { label: 'C', desc: 'Poids lourd / Camion', critical: true },
  D: { label: 'D', desc: 'Transport de personnes / Autobus', critical: true },
  E: { label: 'E', desc: 'Semi-remorque / Remorque lourde (+750kg)', critical: true },
  CE: { label: 'CE', desc: 'Poids lourd + remorque', critical: true },
  EC: { label: 'EC', desc: 'Ensemble articule', critical: true },
  AB: { label: 'AB', desc: 'Vehicule leger + Motos', critical: false },
  F: { label: 'F', desc: 'Adapte handicap', critical: false },
};

// Categorie requise pour transport lourd (3T+ semi-remorques)
const HEAVY_TRANSPORT_CATEGORIES = ['C', 'D', 'E', 'CE', 'EC'];

const getCategoryStatus = (cat) => {
  if (!cat?.expiresAt) return 'unknown';
  if (cat.expiresAt === 'PERMANENT' || cat.expiresAt === 'permanent') return 'permanent';
  const exp = new Date(cat.expiresAt);
  const now = new Date();
  const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'expired';
  if (diffDays <= 30) return 'expiring_soon';
  return 'valid';
};

const getHeavyTransportFitness = (categories) => {
  if (!categories || !Array.isArray(categories) || categories.length === 0) return null;
  const heavyCats = categories.filter(c => HEAVY_TRANSPORT_CATEGORIES.includes(c.category));
  if (heavyCats.length === 0) return { status: 'no_heavy', message: 'Aucune categorie transport lourd' };
  const expired = heavyCats.filter(c => getCategoryStatus(c) === 'expired');
  const expiringSoon = heavyCats.filter(c => getCategoryStatus(c) === 'expiring_soon');
  if (expired.length > 0) return { status: 'expired', message: `${expired.map(c => c.category).join(', ')} expiree(s)`, expired };
  if (expiringSoon.length > 0) return { status: 'expiring', message: `${expiringSoon.map(c => c.category).join(', ')} expire bientot`, expiringSoon };
  return { status: 'valid', message: 'Apte transport lourd' };
};

const DriverForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', phone: '', email: '',
    permitType: 'C', permitNumber: '', permitExpirationDate: '',
    status: 'disponible', photoUrl: '', internalComment: '',
    permitDeliveryDate: '', permitDeliveryPlace: '', bloodGroup: '',
    identityDocType: '', identityDocNumber: '', nationality: '',
    birthDate: '', birthPlace: '', identityExpirationDate: '',
    permitCategories: null,
    // Phase D — Infos Wave (paiement chauffeur)
    waveNumber: '', waveDisplayName: '', financeComment: '', verificationStatus: 'to_verify',
  });

  // Partner
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [currentPartnerName, setCurrentPartnerName] = useState('');

  // OCR state
  const [ocrDocs, setOcrDocs] = useState({ permis_conduire: [], piece_identite: [] });
  const [ocrLoading, setOcrLoading] = useState({});
  const [extractedData, setExtractedData] = useState({ permis_conduire: {}, piece_identite: {} });
  // For creation: store scanned files to upload after save
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    fetchPartners();
    if (isEdit) { fetchDriver(); fetchOcrDocuments(); }
  }, [id]);

  const fetchDriver = async () => {
    try {
      const res = await axios.get(`${API}/drivers/${id}`);
      const d = res.data;
      setFormData({
        firstName: d.firstName || '', lastName: d.lastName || '',
        phone: d.phone && !d.phone.startsWith('no-phone') ? d.phone : '',
        email: d.email || '', permitType: d.permitType || 'C',
        permitNumber: d.permitNumber || '',
        permitExpirationDate: d.permitExpirationDate ? d.permitExpirationDate.split('T')[0] : '',
        status: d.status || 'disponible',
        photoUrl: d.photoUrl || '', internalComment: d.internalComment || '',
        permitDeliveryDate: d.permitDeliveryDate || '',
        permitDeliveryPlace: d.permitDeliveryPlace || '',
        bloodGroup: d.bloodGroup || '',
        identityDocType: d.identityDocType || '',
        identityDocNumber: d.identityDocNumber || '',
        nationality: d.nationality || '',
        birthDate: d.birthDate || '',
        birthPlace: d.birthPlace || '',
        identityExpirationDate: d.identityExpirationDate || '',
        permitCategories: d.permitCategories || null,
        waveNumber: d.waveNumber || '',
        waveDisplayName: d.waveDisplayName || '',
        financeComment: d.financeComment || '',
        verificationStatus: d.verificationStatus || 'to_verify',
      });
      setSelectedPartnerId(d.partnerId || '');
      setCurrentPartnerName(d.partnerName || '');
    } catch {
      toast({ title: "Erreur", description: "Impossible de charger le chauffeur", variant: "destructive" });
    }
  };

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API}/transport-partners?limit=200`, {});
      setPartners(res.data.partners || []);
    } catch { /* ignore */ }
  };

  const fetchOcrDocuments = async () => {
    try {
      const res = await axios.get(`${API}/ocr/documents/entity/driver/${id}`, {});
      const docs = res.data.documents || [];
      const grouped = { permis_conduire: [], piece_identite: [] };
      docs.forEach(d => { if (grouped[d.type]) grouped[d.type].push(d); });
      setOcrDocs(grouped);
      const merged = { permis_conduire: {}, piece_identite: {} };
      docs.forEach(d => {
        if (d.extractedData && d.extractionStatus === 'success' && merged[d.type]) {
          merged[d.type] = { ...merged[d.type], ...d.extractedData };
        }
      });
      setExtractedData(merged);
    } catch { /* ignore */ }
  };

  const handleOcrScan = async (docType, side, file) => {
    if (!file) return;
    const key = `${docType}_${side}`;
    setOcrLoading(prev => ({ ...prev, [key]: true }));
    try {
      let res;
      if (isEdit) {
        // Upload + extract for existing driver
        const fd = new FormData();
        fd.append('file', file); fd.append('type', docType);
        fd.append('side', side); fd.append('entityType', 'driver');
        fd.append('entityId', id); fd.append('extract', 'true');
        res = await axios.post(`${API}/ocr/documents/upload`, fd, {
          headers: { Authorization: 'Bearer cookie-auth', 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Extract only for new driver (no entity yet)
        const fd = new FormData();
        fd.append('file', file); fd.append('type', docType); fd.append('side', side);
        res = await axios.post(`${API}/ocr/extract-only`, fd, {
          headers: { Authorization: 'Bearer cookie-auth', 'Content-Type': 'multipart/form-data' }
        });
        // Save file for later upload after creation
        setPendingFiles(prev => [...prev.filter(p => !(p.type === docType && p.side === side)), { type: docType, side, file }]);
      }
      const doc = res.data;
      if (doc.extractionStatus === 'success' && doc.extractedData) {
        setExtractedData(prev => ({ ...prev, [docType]: { ...prev[docType], ...doc.extractedData } }));
        toast({ title: `Extraction ${side} reussie`, description: `${Object.keys(doc.extractedData).filter(k => doc.extractedData[k]).length} champs extraits` });
      } else if (doc.extractedData?.error) {
        toast({ title: 'Extraction échouée', description: doc.extractedData.error, variant: 'destructive' });
      } else {
        toast({ title: 'Extraction échouée', description: 'Aucun champ extrait', variant: 'destructive' });
      }
      if (isEdit) fetchOcrDocuments();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur upload', variant: 'destructive' });
    }
    setOcrLoading(prev => ({ ...prev, [key]: false }));
  };

  const applyExtracted = (docType) => {
    const d = extractedData[docType];
    if (!d || !Object.keys(d).length) { toast({ title: 'Aucune donnee', variant: 'destructive' }); return; }
    setFormData(prev => {
      const updated = { ...prev };
      if (docType === 'permis_conduire') {
        if (d.permitNumber) updated.permitNumber = d.permitNumber;
        if (d.lastName) updated.lastName = d.lastName;
        if (d.firstName) updated.firstName = d.firstName;
        if (d.birthDate) updated.birthDate = d.birthDate;
        if (d.birthPlace) updated.birthPlace = d.birthPlace;
        if (d.deliveryDate) updated.permitDeliveryDate = d.deliveryDate;
        if (d.deliveryPlace) updated.permitDeliveryPlace = d.deliveryPlace;
        if (d.bloodGroup) updated.bloodGroup = d.bloodGroup;
        if (d.identityDocNumber) updated.identityDocNumber = d.identityDocNumber;
        // Handle categories from recto (simple string list)
        if (d.categories) {
          const cats = typeof d.categories === 'string' ? d.categories.split(',').map(c => c.trim()) : d.categories;
          if (cats?.length) {
            // Pick the highest category (E > D > C > CE > B)
            const priority = ['E', 'EC', 'D', 'CE', 'C', 'B'];
            const best = priority.find(p => cats.includes(p)) || cats[0];
            updated.permitType = best;
          }
        }
        // Handle permitCategories from verso (array of {category, validFrom, expiresAt})
        if (d.permitCategories && Array.isArray(d.permitCategories)) {
          updated.permitCategories = d.permitCategories;
          // Derive best permitType and expiration date from categories
          const priority = ['E', 'EC', 'D', 'CE', 'C', 'B'];
          const catLetters = d.permitCategories.map(c => c.category);
          const best = priority.find(p => catLetters.includes(p)) || catLetters[0];
          if (best) updated.permitType = best;
          // Get the latest non-PERMANENT expiration date
          let latestExp = null;
          d.permitCategories.forEach(c => {
            if (c.expiresAt && c.expiresAt !== 'PERMANENT' && c.expiresAt !== 'permanent') {
              const dateStr = c.expiresAt;
              if (!latestExp || dateStr > latestExp) latestExp = dateStr;
            }
          });
          if (latestExp) updated.permitExpirationDate = latestExp;
        }
      } else if (docType === 'piece_identite') {
        if (d.docType) updated.identityDocType = d.docType;
        if (d.docNumber) updated.identityDocNumber = d.docNumber;
        if (d.lastName) updated.lastName = d.lastName;
        if (d.firstName) updated.firstName = d.firstName;
        if (d.birthDate) updated.birthDate = d.birthDate;
        if (d.birthPlace) updated.birthPlace = d.birthPlace;
        if (d.nationality) updated.nationality = d.nationality;
        if (d.expirationDate) updated.identityExpirationDate = d.expirationDate;
      }
      return updated;
    });
    toast({ title: 'Donnees appliquees au formulaire' });
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/ocr/documents/${docId}`, {});
      toast({ title: 'Document supprimé' });
      fetchOcrDocuments();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.phone || !formData.permitNumber || !formData.permitExpirationDate) {
      toast({ title: "Erreur", description: "Veuillez remplir tous les champs obligatoires", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData, permitExpirationDate: new Date(formData.permitExpirationDate).toISOString() };
      // Remove empty strings but keep permitCategories if present
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
      });
      payload.firstName = formData.firstName;
      payload.lastName = formData.lastName;
      payload.phone = formData.phone;
      payload.permitNumber = formData.permitNumber;
      payload.permitType = formData.permitType;
      // Ensure permitCategories is included if present
      if (formData.permitCategories && Array.isArray(formData.permitCategories) && formData.permitCategories.length > 0) {
        payload.permitCategories = formData.permitCategories;
      }

      let driverId = id;
      if (isEdit) {
        await axios.put(`${API}/drivers/${id}`, payload);
        toast({ title: "Chauffeur modifié" });
      } else {
        const res = await axios.post(`${API}/drivers`, payload);
        driverId = res.data.id || res.data.driverId;
        toast({ title: "Chauffeur créé" });

        // Upload pending scanned files
        if (driverId && pendingFiles.length > 0) {
          for (const pf of pendingFiles) {
            try {
              const fd = new FormData();
              fd.append('file', pf.file); fd.append('type', pf.type);
              fd.append('side', pf.side); fd.append('entityType', 'driver');
              fd.append('entityId', driverId); fd.append('extract', 'false');
              await axios.post(`${API}/ocr/documents/upload`, fd, {
                headers: { Authorization: 'Bearer cookie-auth', 'Content-Type': 'multipart/form-data' }
              });
            } catch { /* ignore individual upload errors */ }
          }
        }
      }

      // Handle partner assignment
      if (driverId && selectedPartnerId && selectedPartnerId !== (isEdit ? (await axios.get(`${API}/drivers/${driverId}`)).data?.partnerId : '')) {
        try {
          await axios.post(`${API}/drivers/${driverId}/assign-partner`, { partnerId: selectedPartnerId }, {});
        } catch { /* ignore */ }
      }

      // Phase D — Sauvegarde des infos Wave (paiement) via endpoint dédié (audit trail)
      if (driverId) {
        const waveInfo = {};
        if (formData.waveNumber !== undefined) waveInfo.waveNumber = formData.waveNumber || null;
        if (formData.waveDisplayName !== undefined) waveInfo.waveDisplayName = formData.waveDisplayName || null;
        if (formData.financeComment !== undefined) waveInfo.financeComment = formData.financeComment || null;
        if (formData.verificationStatus) waveInfo.verificationStatus = formData.verificationStatus;
        try {
          await axios.patch(`${API}/drivers/${driverId}/wave-info`, waveInfo);
        } catch (waveErr) {
          // Ne pas bloquer la sauvegarde principale
          toast({
            title: 'Infos Wave non sauvegardées',
            description: waveErr?.response?.data?.detail || 'Le chauffeur a été enregistré mais les infos Wave n\'ont pas pu être mises à jour.',
            variant: 'default',
          });
        }
      }

      navigate('/admin/drivers');
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible de sauvegarder", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const hasExtracted = (docType) => {
    const d = extractedData[docType];
    return d && Object.keys(d).filter(k => d[k] !== null && d[k] !== undefined && d[k] !== '').length > 0;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/admin/drivers')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900" data-testid="driver-form-title">
            {isEdit ? 'Modifier le chauffeur' : 'Nouveau chauffeur'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isEdit ? 'Informations et documents du chauffeur' : 'Scannez les documents ou remplissez manuellement'}
          </p>
        </div>
      </div>

      {/* OCR Scan Section - Always visible */}
      <Card className="border-emerald-200 bg-emerald-50/20" data-testid="ocr-scan-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ScanLine className="w-4 h-4 text-emerald-600" />
            Scanner les documents
          </CardTitle>
          <p className="text-xs text-gray-500">Scannez recto/verso pour pré-remplir automatiquement le formulaire</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Permis de conduire */}
            <OcrUploadCard
              title="Permis de conduire" icon={CreditCard} color="amber"
              docType="permis_conduire" ocrLoading={ocrLoading}
              docs={ocrDocs.permis_conduire} hasExtracted={hasExtracted('permis_conduire')}
              onScan={handleOcrScan} onApply={() => applyExtracted('permis_conduire')}
              onDelete={handleDeleteDoc} isEdit={isEdit}
            />
            {/* Piece d'identite */}
            <OcrUploadCard
              title="Piece d'identite (CNI)" icon={IdCard} color="blue"
              docType="piece_identite" ocrLoading={ocrLoading}
              docs={ocrDocs.piece_identite} hasExtracted={hasExtracted('piece_identite')}
              onScan={handleOcrScan} onApply={() => applyExtracted('piece_identite')}
              onDelete={handleDeleteDoc} isEdit={isEdit}
            />
          </div>
          {/* Extracted preview */}
          {(hasExtracted('permis_conduire') || hasExtracted('piece_identite')) && (
            <div className="mt-3 p-3 rounded-lg border border-emerald-200 bg-white">
              <p className="text-xs font-semibold text-emerald-700 mb-2">Données extraites — cliquez &quot;Appliquer&quot; pour pré-remplir</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                {[...Object.entries(extractedData.permis_conduire), ...Object.entries(extractedData.piece_identite)]
                  .filter(([_, v]) => v !== null && v !== undefined && v !== '')
                  .map(([key, val]) => (
                    <div key={key} className="text-[11px]">
                      <span className="text-gray-400">{FIELD_LABELS[key] || key}:</span>{' '}
                      <span className="font-medium text-gray-800">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Info */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="w-4 h-4 text-emerald-600" /> Informations personnelles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Prenom *</Label>
                <Input value={formData.firstName} onChange={set('firstName')} required data-testid="driver-firstName" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Nom *</Label>
                <Input value={formData.lastName} onChange={set('lastName')} required data-testid="driver-lastName" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Téléphone *</Label>
                <Input type="tel" value={formData.phone} onChange={set('phone')} placeholder="+225 XX XX XX XX XX" required data-testid="driver-phone" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Email</Label>
                <Input type="email" value={formData.email} onChange={set('email')} placeholder="email@exemple.com" data-testid="driver-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Statut</Label>
                <Select value={formData.status || 'disponible'} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger data-testid="driver-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disponible">Disponible</SelectItem>
                    <SelectItem value="occupe">Occupe</SelectItem>
                    <SelectItem value="en_mission">En mission</SelectItem>
                    <SelectItem value="inactif">Inactif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Groupe sanguin</Label>
                <Input value={formData.bloodGroup} onChange={set('bloodGroup')} placeholder="A+, B-, O+..." data-testid="driver-bloodGroup" />
              </div>
            </div>
            {/* Identity section */}
            <div className="rounded-lg border border-blue-100 bg-blue-50/30 p-4">
              <p className="text-xs font-semibold text-blue-700 mb-3 flex items-center gap-1.5">
                <IdCard className="w-3.5 h-3.5" /> Piece d&apos;identite
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Type de document</Label>
                  <Select value={formData.identityDocType || '_none'} onValueChange={(v) => setFormData(prev => ({ ...prev, identityDocType: v === '_none' ? '' : v }))}>
                    <SelectTrigger data-testid="driver-identityDocType"><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">-- Non renseigne --</SelectItem>
                      <SelectItem value="CNI">CNI</SelectItem>
                      <SelectItem value="Passeport">Passeport</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">N Document</Label>
                  <Input value={formData.identityDocNumber} onChange={set('identityDocNumber')} data-testid="driver-identityDocNumber" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Nationalite</Label>
                  <Input value={formData.nationality} onChange={set('nationality')} placeholder="Ivoirienne" data-testid="driver-nationality" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Date de naissance</Label>
                  <Input type="date" value={formData.birthDate} onChange={set('birthDate')} data-testid="driver-birthDate" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Lieu de naissance</Label>
                  <Input value={formData.birthPlace} onChange={set('birthPlace')} placeholder="Abidjan" data-testid="driver-birthPlace" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Date expiration PI</Label>
                  <Input type="date" value={formData.identityExpirationDate} onChange={set('identityExpirationDate')} data-testid="driver-identityExpDate" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Permit */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="w-4 h-4 text-amber-600" /> Informations du permis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Categorie principale *</Label>
                <Select value={formData.permitType} onValueChange={(v) => setFormData(prev => ({ ...prev, permitType: v }))}>
                  <SelectTrigger data-testid="driver-permitType"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">A (Motos / Tricycles)</SelectItem>
                    <SelectItem value="B">B (Véhicule leger)</SelectItem>
                    <SelectItem value="C">C (Poids lourd)</SelectItem>
                    <SelectItem value="CE">CE (Poids lourd + remorque)</SelectItem>
                    <SelectItem value="D">D (Transport de personnes)</SelectItem>
                    <SelectItem value="E">E (Semi-remorque / Remorque lourde)</SelectItem>
                    <SelectItem value="EC">EC (Ensemble articule)</SelectItem>
                    <SelectItem value="AB">AB (Véhicule + Motos)</SelectItem>
                    <SelectItem value="F">F (Adapte handicap)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">N Permis *</Label>
                <Input value={formData.permitNumber} onChange={set('permitNumber')} required data-testid="driver-permitNumber" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Date expiration generale *</Label>
                <Input type="date" value={formData.permitExpirationDate} onChange={set('permitExpirationDate')} required data-testid="driver-permitExpDate" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Date de delivrance</Label>
                <Input type="date" value={formData.permitDeliveryDate} onChange={set('permitDeliveryDate')} data-testid="driver-permitDeliveryDate" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Lieu de delivrance</Label>
                <Input value={formData.permitDeliveryPlace} onChange={set('permitDeliveryPlace')} placeholder="Abidjan" data-testid="driver-permitDeliveryPlace" />
              </div>
            </div>

            {/* Categories detaillees avec statut */}
            {formData.permitCategories && Array.isArray(formData.permitCategories) && formData.permitCategories.length > 0 && (
              <div className="space-y-3" data-testid="permit-categories-detail">
                {/* Heavy Transport Fitness Alert */}
                {(() => {
                  const fitness = getHeavyTransportFitness(formData.permitCategories);
                  if (!fitness) return null;
                  if (fitness.status === 'expired') return (
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-red-200 bg-red-50" data-testid="heavy-transport-alert-expired">
                      <ShieldAlert className="w-5 h-5 text-red-600 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-red-800">Non apte au transport lourd</p>
                        <p className="text-[11px] text-red-600">Categorie(s) {fitness.message} - Renouvellement obligatoire pour semi-remorques 3T+</p>
                      </div>
                    </div>
                  );
                  if (fitness.status === 'expiring') return (
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50" data-testid="heavy-transport-alert-expiring">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-amber-800">Attention - Expiration proche</p>
                        <p className="text-[11px] text-amber-600">Categorie(s) {fitness.message}</p>
                      </div>
                    </div>
                  );
                  if (fitness.status === 'no_heavy') return (
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50" data-testid="heavy-transport-alert-none">
                      <Truck className="w-5 h-5 text-gray-400 shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-gray-600">Pas de categorie transport lourd (C/D/E)</p>
                        <p className="text-[11px] text-gray-500">Ce chauffeur ne peut pas conduire de poids lourds ou semi-remorques</p>
                      </div>
                    </div>
                  );
                  return (
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50" data-testid="heavy-transport-alert-valid">
                      <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-emerald-800">Apte au transport lourd</p>
                        <p className="text-[11px] text-emerald-600">Categories valides pour semi-remorques 3T+</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Categories Grid */}
                <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                    <p className="text-xs font-semibold text-slate-700">Categories du permis ({formData.permitCategories.length})</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
                    {formData.permitCategories.map((cat, idx) => {
                      const status = getCategoryStatus(cat);
                      const info = PERMIT_CATEGORIES_INFO[cat.category] || {};
                      const isCritical = info.critical;
                      const borderColor = status === 'expired' ? 'border-red-300 bg-red-50/50' :
                        status === 'expiring_soon' ? 'border-amber-300 bg-amber-50/50' :
                        status === 'permanent' ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-100';
                      return (
                        <div key={idx} className={`p-2.5 border ${borderColor} ${idx < formData.permitCategories.length - (formData.permitCategories.length % 2 === 0 ? 2 : 1) ? 'border-b' : ''}`}
                          data-testid={`permit-cat-${cat.category}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-bold text-slate-800">{cat.category}</span>
                            {status === 'expired' && <Badge variant="destructive" className="text-[9px] h-4 px-1.5">Expire</Badge>}
                            {status === 'expiring_soon' && <Badge className="text-[9px] h-4 px-1.5 bg-amber-100 text-amber-800 border border-amber-300">Bientot</Badge>}
                            {status === 'permanent' && <Badge className="text-[9px] h-4 px-1.5 bg-emerald-100 text-emerald-800 border border-emerald-300">Permanent</Badge>}
                            {status === 'valid' && <Badge className="text-[9px] h-4 px-1.5 bg-green-100 text-green-800 border border-green-300">Valide</Badge>}
                          </div>
                          <p className="text-[10px] text-slate-500 mb-1">{info.desc || ''}</p>
                          {isCritical && <span className="inline-flex items-center text-[9px] text-blue-600 font-medium"><Truck className="w-2.5 h-2.5 mr-0.5" />Transport lourd</span>}
                          <div className="mt-1 text-[10px] text-slate-600">
                            {cat.validFrom && <div>Depuis: {cat.validFrom}</div>}
                            <div className={status === 'expired' ? 'text-red-600 font-semibold' : ''}>
                              Exp: {cat.expiresAt || '—'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transporter assignment */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="w-4 h-4 text-blue-600" /> Transporteur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Rattacher a un transporteur</Label>
              <Select value={selectedPartnerId || '_none'} onValueChange={(v) => setSelectedPartnerId(v === '_none' ? '' : v)}>
                <SelectTrigger data-testid="driver-partner">
                  <SelectValue placeholder="Selectionner un transporteur..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-- Aucun transporteur --</SelectItem>
                  {partners.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name || p.companyName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isEdit && currentPartnerName && !selectedPartnerId && (
                <p className="text-[11px] text-amber-600">Actuellement lie a : {currentPartnerName}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Wave (paiement chauffeur) — Phase D */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="w-4 h-4 text-emerald-600" /> Paiement Wave
              {formData.verificationStatus === 'verified' && (
                <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300 ml-1" data-testid="driver-wave-verified-badge">
                  <ShieldCheck className="w-3 h-3 mr-0.5" /> Vérifié
                </Badge>
              )}
              {formData.verificationStatus === 'unverified' && (
                <Badge className="text-[10px] bg-red-100 text-red-800 border-red-300 ml-1" data-testid="driver-wave-unverified-badge">
                  <ShieldAlert className="w-3 h-3 mr-0.5" /> Non vérifié
                </Badge>
              )}
              {formData.verificationStatus === 'to_verify' && (
                <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300 ml-1" data-testid="driver-wave-toverify-badge">
                  <AlertTriangle className="w-3 h-3 mr-0.5" /> À vérifier
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 rounded-lg bg-emerald-50/40 border border-emerald-100 text-[11px] text-emerald-800">
              Ces informations sont utilisées pour créer et rapprocher les paiements Wave envoyés au chauffeur.
              Le <strong>Numéro Wave</strong> peut différer du téléphone principal (compte pro dédié).
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Numéro Wave</Label>
                <Input
                  type="tel"
                  value={formData.waveNumber}
                  onChange={set('waveNumber')}
                  placeholder="+225 XX XX XX XX XX"
                  data-testid="driver-waveNumber"
                />
                <p className="text-[10px] text-slate-500">Numéro associé au compte Wave (si différent du téléphone).</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Nom affiché dans Wave</Label>
                <Input
                  value={formData.waveDisplayName}
                  onChange={set('waveDisplayName')}
                  placeholder="Ex: KOUADIO KOFFI"
                  data-testid="driver-waveDisplayName"
                />
                <p className="text-[10px] text-slate-500">Nom du bénéficiaire tel qu&apos;il apparaît dans le relevé Wave.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Statut de vérification</Label>
                <Select
                  value={formData.verificationStatus || 'to_verify'}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, verificationStatus: v }))}
                >
                  <SelectTrigger data-testid="driver-verificationStatus"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="to_verify">À vérifier</SelectItem>
                    <SelectItem value="verified">Vérifié</SelectItem>
                    <SelectItem value="unverified">Non vérifié</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Commentaire finance</Label>
                <Input
                  value={formData.financeComment}
                  onChange={set('financeComment')}
                  placeholder="Note interne équipe finance"
                  data-testid="driver-financeComment"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Optional */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Informations complementaires</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">URL de la photo</Label>
              <Input type="url" value={formData.photoUrl} onChange={set('photoUrl')} placeholder="https://..." data-testid="driver-photoUrl" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Commentaire interne</Label>
              <Textarea value={formData.internalComment} onChange={set('internalComment')} placeholder="Notes internes..." rows={3} data-testid="driver-comment" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/drivers')} data-testid="driver-cancel-btn">Annuler</Button>
          <Button type="submit" disabled={loading} className="bg-[#1db584] hover:bg-[#16a075]" data-testid="driver-submit-btn">
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Enregistrement...' : (isEdit ? 'Mettre a jour' : 'Creer le chauffeur')}
          </Button>
        </div>
      </form>
    </div>
  );
};

// OCR Upload Card component
const OcrUploadCard = ({ title, icon: Icon, color, docType, ocrLoading, docs, hasExtracted, onScan, onApply, onDelete, isEdit }) => {
  const rectoDoc = docs.find(d => d.side === 'recto');
  const versoDoc = docs.find(d => d.side === 'verso');

  return (
    <div className="border rounded-lg p-3 bg-white" data-testid={`doc-section-${docType}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`flex items-center gap-1.5 text-xs font-semibold text-${color}-700`}>
          <Icon className="w-3.5 h-3.5" /> {title}
        </span>
        {hasExtracted && (
          <Button size="sm" variant="outline" onClick={onApply}
            className={`h-6 text-[10px] px-2 text-${color}-700 border-${color}-300`} data-testid={`apply-ocr-${docType}`}>
            <Download className="w-3 h-3 mr-1" /> Appliquer
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {['recto', 'verso'].map(side => {
          const key = `${docType}_${side}`;
          const isLoading = ocrLoading[key];
          const existing = side === 'recto' ? rectoDoc : versoDoc;
          return (
            <label key={side} className={`relative block border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors
              ${isLoading ? 'border-emerald-300 bg-emerald-50' : existing ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300'}`}
              data-testid={`ocr-upload-${docType}-${side}`}>
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={(e) => onScan(docType, side, e.target.files?.[0])} disabled={isLoading} />
              {isLoading ? (
                <div className="flex flex-col items-center gap-1">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                  <span className="text-[10px] text-emerald-600">Extraction...</span>
                </div>
              ) : existing ? (
                <div className="flex flex-col items-center gap-1">
                  <Check className="w-5 h-5 text-emerald-500" />
                  <span className="text-[10px] text-emerald-600">{side} scanne</span>
                  {existing.extractionStatus === 'success' && <Badge variant="secondary" className="text-[9px] bg-emerald-100 text-emerald-700">OCR OK</Badge>}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <ScanLine className="w-5 h-5 text-gray-400" />
                  <span className="text-[10px] text-gray-500">Scanner {side}</span>
                </div>
              )}
            </label>
          );
        })}
      </div>
      {/* Doc actions */}
      {isEdit && (rectoDoc || versoDoc) && (
        <div className="flex gap-3 mt-2 justify-center">
          {[rectoDoc, versoDoc].filter(Boolean).map(d => (
            <div key={d.id} className="flex items-center gap-1.5">
              <a href={`${BACKEND_URL}${d.fileUrl}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5">
                <Eye className="w-3 h-3" /> {d.side}
              </a>
              <button onClick={() => onDelete(d.id)} className="text-[10px] text-red-500 hover:underline flex items-center gap-0.5" data-testid={`delete-doc-${docType}-${d.side}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FIELD_LABELS = {
  lastName: 'Nom', firstName: 'Prenoms', birthDate: 'Date naissance',
  birthPlace: 'Lieu naissance', permitNumber: 'N Permis',
  deliveryDate: 'Date delivrance', deliveryPlace: 'Lieu delivrance',
  categories: 'Categories', restrictions: 'Restrictions',
  permitCategories: 'Categories detaillees', identityDocNumber: 'N PI',
  bloodGroup: 'Groupe sanguin', docType: 'Type document', docNumber: 'N Document',
  nationality: 'Nationalite', expirationDate: 'Date expiration',
};

export default DriverForm;
